/**
 * Локальное хранилище приложения: снимок с сервера и очередь на отправку.
 *
 * IndexedDB, а не SQLite: данных мало — сотни задач и десятки клиентов, — а
 * плагин SQLite тянул бы отдельную сборку под каждую архитектуру Android.
 *
 * Две области с разной судьбой:
 *   snapshot — то, что пришло с сервера. Перезаписывается целиком при каждой
 *              удачной синхронизации, конфликтов не бывает.
 *   outbox   — то, что сделал техник без связи. Копится локально, уходит по
 *              одной операции при появлении сети, удаляется после подтверждения.
 */

const DB_NAME = 'timetrack'
const DB_VERSION = 1

/** Что лежит в снимке. Ключ — имя раздела, значение — массив записей. */
export type SnapshotPart =
  | 'tasks'
  | 'clients'
  | 'contacts'
  | 'accesses'
  | 'sources'
  | 'products'
  | 'portal_services'
  /** Задачи, созданные без связи. Живут отдельно от снимка: снимок сервер
   *  перезаписывает целиком, и черновик в нём пропал бы до подтверждения. */
  | 'draft_tasks'

/** Операция, ждущая отправки на сервер. */
export interface OutboxOp {
  /** Локальный номер, растёт — очередь отправляется в порядке появления. */
  id?: number
  /** Идентификатор операции, придуманный на телефоне. Сервер по нему отличает
   *  повтор от новой записи, поэтому переотправка безопасна. */
  clientUid: string
  kind:
    | 'timer.start'
    | 'timer.stop'
    | 'task.create'
    | 'task.update'
    | 'task.status'
    | 'task.estimate'
  payload: Record<string, unknown>
  createdAt: string
  /** Сколько раз пытались отправить — чтобы не долбить сервер вечно. */
  attempts: number
  /** Последняя ошибка: показываем технику, если операция так и не прошла. */
  lastError?: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('snapshot')) {
        // Ключ — имя раздела: снимок кладётся и читается целиком
        db.createObjectStore('snapshot')
      }
      if (!db.objectStoreNames.contains('outbox')) {
        const store = db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true })
        store.createIndex('clientUid', 'clientUid', { unique: true })
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta')
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode)
        const req = run(t.objectStore(store))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      }),
  )
}

// ---------- снимок ----------

export async function putSnapshotPart(part: SnapshotPart, rows: unknown[]): Promise<void> {
  await tx('snapshot', 'readwrite', (s) => s.put(rows, part))
}

export async function getSnapshotPart<T>(part: SnapshotPart): Promise<T[]> {
  const rows = await tx<T[] | undefined>('snapshot', 'readonly', (s) => s.get(part))
  return rows ?? []
}

/** Когда снимок последний раз обновлялся. По серверному времени, не по часам
 *  телефона: они могут врать, а «данные от 3 сентября» должно значить именно это. */
export async function getSyncedAt(): Promise<string | null> {
  return (await tx<string | undefined>('meta', 'readonly', (s) => s.get('syncedAt'))) ?? null
}

export async function setSyncedAt(serverTime: string): Promise<void> {
  await tx('meta', 'readwrite', (s) => s.put(serverTime, 'syncedAt'))
}

/**
 * Соответствие «черновик на телефоне — запись на сервере».
 *
 * Задача, созданная без связи, получает временный отрицательный номер. Пока
 * сервер не ответил, по ней уже могли запустить таймер или сменить статус.
 * Когда создание уходит, сюда ложится настоящий номер, и очередь подставляет
 * его в последующие операции вместо временного.
 */
export async function rememberServerId(localId: number, serverId: number): Promise<void> {
  const map = await getIdMap()
  map[String(localId)] = serverId
  await tx('meta', 'readwrite', (s) => s.put(map, 'idmap'))
}

export async function getIdMap(): Promise<Record<string, number>> {
  return (await tx<Record<string, number> | undefined>('meta', 'readonly', (s) => s.get('idmap'))) ?? {}
}

// ---------- очередь ----------

export async function enqueue(op: Omit<OutboxOp, 'id' | 'attempts'>): Promise<void> {
  await tx('outbox', 'readwrite', (s) => s.add({ ...op, attempts: 0 }))
}

export async function listOutbox(): Promise<OutboxOp[]> {
  const all = await tx<OutboxOp[]>('outbox', 'readonly', (s) => s.getAll())
  return all.sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
}

export async function dropFromOutbox(id: number): Promise<void> {
  await tx('outbox', 'readwrite', (s) => s.delete(id))
}

export async function markOutboxFailure(op: OutboxOp, error: string): Promise<void> {
  await tx('outbox', 'readwrite', (s) =>
    s.put({ ...op, attempts: op.attempts + 1, lastError: error }),
  )
}

export async function outboxSize(): Promise<number> {
  return tx<number>('outbox', 'readonly', (s) => s.count())
}

// ---------- очистка ----------

/**
 * Стереть всё локальное. Вызывается при выходе и после пяти неверных вводов
 * кода: пароли клиентов не должны оставаться на телефоне, который больше не
 * подтверждает, что он в руках хозяина.
 *
 * Очередь стирается вместе со снимком — это осознанно: несохранённые операции
 * теряются, но оставлять их на устройстве, доступ к которому под вопросом,
 * хуже. Перед выходом приложение показывает, сколько операций не отправлено.
 */
export async function wipeLocal(): Promise<void> {
  const db = await open()
  await Promise.all(
    ['snapshot', 'outbox', 'meta'].map(
      (name) =>
        new Promise<void>((resolve, reject) => {
          const t = db.transaction(name, 'readwrite')
          const req = t.objectStore(name).clear()
          req.onsuccess = () => resolve()
          req.onerror = () => reject(req.error)
        }),
    ),
  )
}
